import { useCallback, useState } from "react";
import {
  createInitialCouponState,
  type MerchantCouponUIState,
} from "../data/merchantCouponEditor.data";
import {
  validateMerchantCoupon,
  type ValidationResult,
} from "../utils/merchantCouponEditor.utils";
import type { MerchantCouponValidationErrors } from "../schemas/merchantCouponEditor.schema";

type CouponFieldValue = MerchantCouponUIState[keyof MerchantCouponUIState];

interface UseMerchantCouponEditorFormReturn {
  coupon: MerchantCouponUIState;
  updateField: (field: keyof MerchantCouponUIState, value: CouponFieldValue) => void;
  addTerm: () => void;
  removeTerm: (index: number) => void;
  updateTerm: (index: number, value: string) => void;
  resetCoupon: () => void;
  loadCoupon: (coupon: MerchantCouponUIState) => void;
  validationErrors: MerchantCouponValidationErrors;
  validateCoupon: () => ValidationResult;
}

export const useMerchantCouponEditorForm = (): UseMerchantCouponEditorFormReturn => {
  const [coupon, setCoupon] = useState<MerchantCouponUIState>(() => createInitialCouponState());
  const [validationErrors, setValidationErrors] = useState<MerchantCouponValidationErrors>({});

  const updateField = useCallback((field: keyof MerchantCouponUIState, value: CouponFieldValue) => {
    setCoupon((current) => ({ ...current, [field]: value }) as MerchantCouponUIState);
    setValidationErrors((current) => {
      if (!current[field]) return current;
      const { [field]: _removedError, ...remainingErrors } = current;
      return remainingErrors;
    });
  }, []);

  const addTerm = useCallback(() => {
    setCoupon((current) => ({
      ...current,
      terms_and_conditions: [...current.terms_and_conditions, ""],
    }));
    setValidationErrors((current) => ({ ...current, terms_and_conditions: undefined }));
  }, []);

  const removeTerm = useCallback((index: number) => {
    setCoupon((current) => {
      if (current.terms_and_conditions.length <= 1) return current;
      return {
        ...current,
        terms_and_conditions: current.terms_and_conditions.filter(
          (_, termIndex) => termIndex !== index,
        ),
      };
    });
    setValidationErrors((current) => ({ ...current, terms_and_conditions: undefined }));
  }, []);

  const updateTerm = useCallback((index: number, value: string) => {
    setCoupon((current) => ({
      ...current,
      terms_and_conditions: current.terms_and_conditions.map((term, termIndex) =>
        termIndex === index ? value : term,
      ),
    }));
    setValidationErrors((current) => ({ ...current, terms_and_conditions: undefined }));
  }, []);

  const resetCoupon = useCallback(() => {
    setCoupon(createInitialCouponState());
    setValidationErrors({});
  }, []);

  const loadCoupon = useCallback((nextCoupon: MerchantCouponUIState) => {
    setCoupon(nextCoupon);
    setValidationErrors({});
  }, []);

  const validateCoupon = useCallback(() => {
    const result = validateMerchantCoupon(coupon);
    setValidationErrors(result.fieldErrors);
    return result;
  }, [coupon]);

  return {
    coupon,
    updateField,
    addTerm,
    removeTerm,
    updateTerm,
    resetCoupon,
    loadCoupon,
    validationErrors,
    validateCoupon,
  };
};
